import { useEffect, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { openPdf } from "@/lib/pdf";
import api from "@/lib/api";
import { ClipboardList, Download, Sun, Sunset, Moon, CheckCircle2, RotateCcw } from "lucide-react";

const ACCENT = "#DB2777";
const SHIFT_ICON = { morning: Sun, afternoon: Sunset, night: Moon };
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }) : "");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

export default function PickConfirmations() {
  const { activeSite } = useSite();
  const [date, setDate] = useState("");
  const [shift, setShift] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d, sh) => {
    setLoading(true);
    try {
      const { data } = await api.get("/pick-confirmations", { params: { date: d || undefined, shift: sh || "all", site: activeSite } });
      setData(data); setDate(data.date);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(date, shift); /* eslint-disable-next-line */ }, [activeSite]);

  if (loading && !data) return <Loader label="Loading pick confirmations…" />;
  if (!data) return null;
  const dl = () => openPdf(`/pick-confirmations/csv?date=${data.date}&shift=${shift}&site=${activeSite}`);

  return (
    <div data-testid="pick-confirmations">
      <PageHeader title="Pick Confirmations" subtitle={`Audit trail of completed picks by shift for ${data.scope}. Every line shows the operator, delivery and sales order.`} icon={ClipboardList} accent={ACCENT}>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="pc-date" value={date} onChange={(e) => { setDate(e.target.value); load(e.target.value, shift); }} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
            {data.available_dates.map((d) => <option key={d} value={d}>{fmtDate(d)}</option>)}
          </select>
          <select data-testid="pc-shift" value={shift} onChange={(e) => { setShift(e.target.value); load(date, e.target.value); }} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
            <option value="all">All shifts</option>
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
            <option value="night">Night</option>
          </select>
          <button data-testid="pc-csv-btn" onClick={dl} className="inline-flex items-center gap-2 rounded-lg bg-[#DB2777] px-4 py-2 text-sm font-semibold text-white hover:bg-[#be1e63]"><Download size={16} /> Export CSV</button>
        </div>
      </PageHeader>

      <div className="mb-6 grid grid-cols-3 gap-4">
        <KpiCard label="Picks confirmed" value={data.totals.lines} accent={ACCENT} />
        <KpiCard label="Operators" value={data.totals.operators} accent="#0a6ed1" />
        <KpiCard label="First-time accuracy" value={`${data.totals.accuracy}%`} accent="#107e3e" />
      </div>

      {data.shifts.every((s) => s.lines === 0) && (
        <div data-testid="pc-empty" className="rounded-xl border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No picks confirmed on this date.</div>
      )}

      <div className="space-y-6">
        {data.shifts.filter((s) => s.lines > 0 || shift !== "all").map((s) => {
          const Icon = SHIFT_ICON[s.key] || Sun;
          return (
            <section key={s.key} data-testid={`pc-shift-${s.key}`} className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#f0f2f5] bg-[#fafbfc] px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#32363a]"><Icon size={16} className="text-[#DB2777]" /> {s.label}</div>
                <div className="flex gap-4 text-xs text-[#6a6d70]">
                  <span><b className="text-[#32363a]">{s.lines}</b> picks</span>
                  <span><b className="text-[#32363a]">{s.operators}</b> operators</span>
                  <span>accuracy <b className={s.accuracy >= 98 ? "text-emerald-600" : "text-amber-600"}>{s.accuracy}%</b></span>
                </div>
              </div>
              {s.lines === 0 ? <div className="px-4 py-6 text-center text-xs text-[#89919a]">No picks in this shift.</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-[#6a6d70]"><tr className="border-b border-[#f0f2f5]">
                      <th className="px-3 py-2 text-left">Time</th><th className="px-3 py-2 text-left">Operator</th>
                      <th className="px-3 py-2 text-left">Delivery</th><th className="px-3 py-2 text-left">Sales Order</th>
                      <th className="px-3 py-2 text-left">Material</th><th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-center">Site</th><th className="px-3 py-2 text-center">First try</th>
                    </tr></thead>
                    <tbody>
                      {s.picks.map((p, i) => (
                        <tr key={i} className="border-b border-[#f5f6f8] last:border-0 hover:bg-[#fafbfc]">
                          <td className="whitespace-nowrap px-3 py-1.5 text-[#6a6d70]">{fmtTime(p.at)}</td>
                          <td className="px-3 py-1.5 font-medium text-[#32363a]">{p.operator}</td>
                          <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[#0a6ed1]">{p.delivery_no || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[#32363a]">{p.order_no || "—"}</td>
                          <td className="px-3 py-1.5 text-[#454b52]">{p.material_name}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{p.qty}</td>
                          <td className="px-3 py-1.5 text-center font-mono text-[11px] text-[#6a6d70]">{p.site_id}</td>
                          <td className="px-3 py-1.5 text-center">{p.first_try ? <CheckCircle2 size={15} className="mx-auto text-emerald-600" /> : <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><RotateCcw size={12} /> retry</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
